import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Testimonials.css';

// SVG Star component — professional gold star rating
const StarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const Testimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [visibleElements, setVisibleElements] = useState([]);
  const sectionRef = useRef(null);
  const autoPlayRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      content: "Mediway Nextgen's hospital management software has completely transformed our hospital operations. The patient management system is now effortless, and staff productivity has improved by 45%. This HMS software is simple to use and patients find the mobile app extremely helpful.",
      author: {
        name: "Dr. Anita Verma",
        role: "Chief Medical Officer",
        hospital: "City Hospital",
        location: "Gorakhpur, Uttar Pradesh",
        avatar: "👩‍⚕️"
      },
      rating: 5,
      metrics: {
        improvement: "45%",
        category: "Staff Productivity"
      }
    },
    {
      id: 2,
      content: "The analytics and reporting features in this hospital software have given us deep insights into our operations. We have reduced patient waiting times by 30% and improved overall satisfaction significantly.",
      author: {
        name: "Rajesh Gupta",
        role: "Hospital Administrator",
        hospital: "Madhuri Nursing Home",
        location: "Deoria, Uttar Pradesh",
        avatar: "👨‍💼"
      },
      rating: 5,
      metrics: {
        improvement: "30%",
        category: "Wait Time Reduction"
      }
    },
    {
      id: 3,
      content: "Implementation of this medical billing software was smooth and the support team is excellent. Our billing accuracy has improved a lot, and we are now collecting payments 55% faster than before.",
      author: {
        name: "Dr. Seema Mishra",
        role: "Medical Director",
        hospital: "Surya Healthcare",
        location: "Varanasi, Uttar Pradesh",
        avatar: "👩‍⚕️"
      },
      rating: 5,
      metrics: {
        improvement: "55%",
        category: "Payment Collection"
      }
    },
    {
      id: 4,
      content: "The HIPAA and NABH compliance features in this EMR software along with strong security give us complete confidence. The healthcare management system is robust, reliable, and has never failed us in over two years of use.",
      author: {
        name: "Abhishek Singh",
        role: "IT Head",
        hospital: "Lifecare Hospital",
        location: "Lucknow, Uttar Pradesh",
        avatar: "👨‍💻"
      },
      rating: 5,
      metrics: {
        improvement: "99.9%",
        category: "System Uptime"
      }
    },
    {
      id: 5,
      content: "Managing our multiple facilities with this cloud-based hospital management system has never been easier. We can now coordinate care seamlessly across all our centers, and the reporting gives us a unified view of operations.",
      author: {
        name: "Dr. Kavita Shukla",
        role: "Regional Medical Director",
        hospital: "Jankalyan Health Network",
        location: "Azamgarh, Uttar Pradesh",
        avatar: "👩‍⚕️"
      },
      rating: 5,
      metrics: {
        improvement: "60%",
        category: "Operational Efficiency"
      }
    }
  ];
  

  const stats = [
    { value: "5+", label: "Healthcare Facilities" },
    { value: "10k+", label: "Patients Served" },
    { value: "99.9%", label: "System Uptime" },
    { value: "24/7", label: "Support Available" }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.dataset.element;
            setVisibleElements(prev => [...prev, elementId]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = sectionRef.current?.querySelectorAll('[data-element]');
    elements?.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  // Auto-play testimonials
  useEffect(() => {
    autoPlayRef.current = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [testimonials.length]);

  const goToTestimonial = (index) => {
    setActiveTestimonial(index);
    // Reset autoplay
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
    }
  };

  const nextTestimonial = () => {
    goToTestimonial((activeTestimonial + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    goToTestimonial(activeTestimonial === 0 ? testimonials.length - 1 : activeTestimonial - 1);
  };

  return (
    <section id="testimonials" className="testimonials" ref={sectionRef}>
      <Helmet>
        <title>Testimonials — Mediway Nextgen HMS</title>
        <meta name="description" content="Customer success stories and testimonials showing how hospitals across India improved operations with Mediway Nextgen." />
        <link rel="canonical" href="https://mediwaynextgen.wayug.com/#testimonials" />
      </Helmet>
      <div className="container">
        <div 
          className={`section-header ${visibleElements.includes('header') ? 'section-header--visible' : ''}`}
          data-element="header"
        >
          <h2 className="section-title">
            Trusted by Healthcare Leaders
          </h2>
          <p className="section-subtitle">
            See how hospitals and healthcare systems are transforming their operations with Mediway Nextgen HMS
          </p>
        </div>

        {/* Stats Section */}
        <div 
          className={`testimonials-stats ${visibleElements.includes('stats') ? 'testimonials-stats--visible' : ''}`}
          data-element="stats"
        >
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Testimonial */}
        <div 
          className={`testimonial-main ${visibleElements.includes('main') ? 'testimonial-main--visible' : ''}`}
          data-element="main"
        >
          <div className="testimonial-container">
            <div className="testimonial-content">
              <div className="quote-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                </svg>
              </div>

              <div className="testimonial-text">
                <p className="testimonial-quote">
                  {testimonials[activeTestimonial].content}
                </p>

                <div className="testimonial-rating">
                  {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                    <StarIcon key={i} />
                  ))}
                  <span className="verified-badge">✓ Verified Customer</span>
                </div>
              </div>

              <div className="testimonial-author">
                <div className="author-avatar">
                  <span>{testimonials[activeTestimonial].author.avatar}</span>
                </div>
                <div className="author-info">
                  <div className="author-name">{testimonials[activeTestimonial].author.name}</div>
                  <div className="author-role">{testimonials[activeTestimonial].author.role}</div>
                  <div className="author-hospital">{testimonials[activeTestimonial].author.hospital}</div>
                  <div className="author-location">{testimonials[activeTestimonial].author.location}</div>
                </div>
              </div>

              <div className="testimonial-metric">
                <div className="metric-value">{testimonials[activeTestimonial].metrics.improvement}</div>
                <div className="metric-label">{testimonials[activeTestimonial].metrics.category}</div>
              </div>
            </div>

            {/* Navigation */}
            <div className="testimonial-navigation">
              <button 
                className="nav-button nav-button--prev"
                onClick={prevTestimonial}
                aria-label="Previous testimonial"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="testimonial-indicators">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator ${activeTestimonial === index ? 'indicator--active' : ''}`}
                    onClick={() => goToTestimonial(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button 
                className="nav-button nav-button--next"
                onClick={nextTestimonial}
                aria-label="Next testimonial"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Testimonial Grid */}
        <div 
          className={`testimonials-grid ${visibleElements.includes('grid') ? 'testimonials-grid--visible' : ''}`}
          data-element="grid"
        >
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <div key={testimonial.id} className="testimonial-card">
              <div className="card-rating">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <StarIcon key={i} />
                ))}
                <span className="verified-badge">✓ Verified</span>
              </div>

              <p className="card-quote">
                "{testimonial.content.substring(0, 120)}..."
              </p>

              <div className="card-author">
                <div className="card-avatar">
                  <span>{testimonial.author.avatar}</span>
                </div>
                <div className="card-info">
                  <div className="card-name">{testimonial.author.name}</div>
                  <div className="card-hospital">{testimonial.author.hospital}</div>
                </div>
              </div>

              <div className="card-metric">
                <span className="metric-number">{testimonial.metrics.improvement}</span>
                <span className="metric-text">{testimonial.metrics.category}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="testimonials-background">
        <div className="background-pattern"></div>
        <div className="background-glow"></div>
      </div>
    </section>
  );
};

export default Testimonials;